import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { esNativo } from "../lib/nativo";

export type ThemeMode = "light" | "dark";

type ThemeContextValue = {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggle: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);
const STORAGE_KEY = "forja-theme";

function getInitialMode(): ThemeMode {
  if (typeof window === "undefined") return "light";
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved === "dark" || saved === "light") return saved;
  // ADENTRO DE LA APP (iPhone) SE ARRANCA EN OSCURO. La app de Android es
  // navy de punta a punta y la pantalla de ingreso de la de iPhone la copia
  // 1:1; con el default claro de la web, el primer paso después de entrar
  // («¿Qué venís a hacer?») saltaba a blanco, y la primera testera lo
  // describió como "una pantalla que no es del estilo de la app" (3-11).
  // Es sólo el punto de partida: el cambio claro/oscuro sigue a mano.
  return esNativo() ? "dark" : "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(getInitialMode);

  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    window.localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const value = useMemo(() => ({ mode, setMode, toggle: () => setMode((current) => current === "light" ? "dark" : "light") }), [mode]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used inside ThemeProvider");
  return context;
}
